import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Camera, X, Upload, Save, WifiOff, CheckCircle2 } from "lucide-react";
import { DatePicker } from "@/components/DatePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAutosaveDraft, loadDraft, clearDraft } from "@/lib/drafts";

const PHOTO_CATEGORIES = ["Storefront", "MLILY display", "Product display", "Floor stock", "Competitor display", "Damaged product", "Promotional material", "General store photo"];
const CONDITIONS = ["Excellent", "Good", "Average", "Poor"];
const OUTCOMES = ["Successful", "Follow-up Required", "No Interest", "Order Placed", "Pending"];
const PURPOSES = [
  "General store visit", "Sales meeting", "Product presentation", "Product training",
  "Staff training", "Display inspection", "New store setup", "Customer complaint",
  "Account follow-up", "Promotional campaign", "Regional trip visit",
];

const EMPTY = {
  customer_id: "", visit_date: new Date().toISOString().slice(0, 10), arrival_time: "", departure_time: "",
  store_contact: "", person_met: "", person_position: "",
  visit_purpose: "General store visit", visit_notes: "",
  products_discussed: [], products_displayed: [], new_products_presented: [], competitor_brands: [], competitor_activity: "",
  floor_stock_condition: "Good", display_condition: "Good", training_provided: "",
  customer_feedback: "", potential_sales_opportunity: "", estimated_opportunity_value: 0,
  expected_order_date: "", issues_found: "", actions_required: "",
  followup_required: false, followup_date: "", next_visit_date: "",
  visit_outcome: "Successful", trip_id: "", draft: false,
};

export default function StoreVisitForm() {
  const { id } = useParams(); // if present, we're editing
  const nav = useNavigate();
  const editing = !!id;
  const draftKey = editing ? `visit_${id}` : "visit_new";

  const [customers, setCustomers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Load lists
  useEffect(() => {
    api.get("/customers").then(r => setCustomers(r.data));
    api.get("/trips").then(r => setTrips(r.data));
  }, []);

  // Load existing visit or local draft
  useEffect(() => {
    (async () => {
      if (editing) {
        try {
          const { data } = await api.get(`/visits/${id}`);
          setF({ ...EMPTY, ...data });
        } catch { toast.error("Could not load visit"); }
      } else {
        const d = loadDraft(draftKey);
        if (d?.value) {
          setF({ ...EMPTY, ...d.value });
          toast.info("Restored unsaved draft");
        }
      }
      setLoaded(true);
    })();
  }, [id, editing, draftKey]);

  const { savedAt, online } = useAutosaveDraft(draftKey, f, loaded);

  // On coming back online, attempt to submit an offline-created draft (only for new form)
  useEffect(() => {
    if (!editing && online && loaded) {
      const pending = loadDraft("visit_offline_pending");
      if (pending?.value?.customer_id) {
        (async () => {
          try {
            await api.post("/visits", { ...pending.value, draft: true });
            clearDraft("visit_offline_pending");
            toast.success("Offline visit synced");
          } catch {}
        })();
      }
    }
  }, [online, editing, loaded]);

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const updList = (k) => (e) => setF({ ...f, [k]: e.target.value.split(",").map(s => s.trim()).filter(Boolean) });
  const updDate = (k) => (v) => setF({ ...f, [k]: v });
  const updSelect = (k) => (v) => setF({ ...f, [k]: v });

  const addPhotos = (e) => {
    const files = Array.from(e.target.files);
    setPhotos([...photos, ...files.map(file => ({ file, preview: URL.createObjectURL(file), category: "General store photo" }))]);
  };
  const removePhoto = (i) => setPhotos(photos.filter((_, idx) => idx !== i));

  const submit = async (asDraft) => {
    if (!f.customer_id) return toast.error("Select a customer");
    setBusy(true);
    try {
      const payload = { ...f, estimated_opportunity_value: Number(f.estimated_opportunity_value) || 0, draft: !!asDraft };
      ["expected_order_date", "followup_date", "next_visit_date"].forEach(k => { if (!payload[k]) payload[k] = null; });
      if (payload.trip_id === "none") payload.trip_id = null;

      let visitId = id;
      if (editing) {
        await api.patch(`/visits/${id}`, payload);
      } else {
        // offline handling
        if (!online) {
          const { saveDraft } = await import("@/lib/drafts");
          saveDraft("visit_offline_pending", payload);
          toast.info("Offline — visit saved locally, will sync when online");
          clearDraft(draftKey);
          nav("/visits");
          return;
        }
        const { data: visit } = await api.post("/visits", payload);
        visitId = visit.id;
      }
      // upload photos (only new local ones)
      for (const p of photos) {
        const fd = new FormData();
        fd.append("file", p.file);
        fd.append("customer_id", f.customer_id);
        fd.append("visit_id", visitId);
        fd.append("category", p.category);
        await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      clearDraft(draftKey);
      toast.success(asDraft ? "Draft saved" : editing ? "Visit updated" : "Visit submitted");
      nav("/visits");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save visit");
    } finally { setBusy(false); }
  };

  const L = ({ label, children }) => (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
  const inp = "w-full h-12 px-3 border border-slate-300 rounded-lg focus:border-[#003DA5] outline-none";

  const Sh = ({ value, onChange, options, placeholder, testid }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-12 border-slate-300 bg-white" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="bg-white">{options.map(o => <SelectItem key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>{typeof o === "string" ? o : o.label}</SelectItem>)}</SelectContent>
    </Select>
  );

  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(false); }} className="space-y-6" data-testid="visit-form">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl sm:text-4xl font-heading font-black tracking-tight">
          {editing ? (f.draft ? "Resume Draft Visit" : "Edit Store Visit") : "Record Store Visit"}
        </h1>
        <div className="text-xs flex items-center gap-2">
          {!online && <span className="badge-warning px-2 py-1 rounded-full flex items-center gap-1"><WifiOff className="w-3 h-3" /> Offline</span>}
          {savedAt && <span className="text-slate-500 flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-600" /> Auto-saved</span>}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h3 className="font-heading font-semibold">Visit Details</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <L label="Customer / Store *">
            <Sh value={f.customer_id} onChange={updSelect("customer_id")} options={customers.map(c => ({ value: c.id, label: `${c.name} — ${c.town}` }))} placeholder="— Select store —" testid="visit-customer-select" />
          </L>
          <L label="Trip (optional)">
            <Sh value={f.trip_id} onChange={updSelect("trip_id")} options={[{ value: "none", label: "— None —" }, ...trips.map(t => ({ value: t.id, label: t.name }))]} placeholder="— None —" />
          </L>
          <L label="Visit Purpose"><Sh value={f.visit_purpose} onChange={updSelect("visit_purpose")} options={PURPOSES} /></L>
          <L label="Visit Date *"><DatePicker value={f.visit_date} onChange={updDate("visit_date")} testid="visit-date" /></L>
          <L label="Person Met"><input value={f.person_met} onChange={upd("person_met")} className={inp} data-testid="visit-person-met" /></L>
          <L label="Person Position"><input value={f.person_position} onChange={upd("person_position")} className={inp} /></L>
          <L label="Arrival Time"><input type="time" value={f.arrival_time} onChange={upd("arrival_time")} className={inp} /></L>
          <L label="Departure Time"><input type="time" value={f.departure_time} onChange={upd("departure_time")} className={inp} /></L>
        </div>
        <L label="Visit Notes"><textarea value={f.visit_notes} onChange={upd("visit_notes")} rows={3} className="w-full p-3 border border-slate-300 rounded-lg" data-testid="visit-notes" /></L>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h3 className="font-heading font-semibold">In-Store Assessment</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <L label="Products Discussed"><input value={f.products_discussed.join(", ")} onChange={updList("products_discussed")} className={inp} placeholder="comma-separated" /></L>
          <L label="Products Currently Displayed"><input value={f.products_displayed.join(", ")} onChange={updList("products_displayed")} className={inp} /></L>
          <L label="New Products Presented"><input value={f.new_products_presented.join(", ")} onChange={updList("new_products_presented")} className={inp} /></L>
          <L label="Competitor Brands"><input value={f.competitor_brands.join(", ")} onChange={updList("competitor_brands")} className={inp} /></L>
          <L label="Floor Stock Condition"><Sh value={f.floor_stock_condition} onChange={updSelect("floor_stock_condition")} options={CONDITIONS} /></L>
          <L label="Display Condition"><Sh value={f.display_condition} onChange={updSelect("display_condition")} options={CONDITIONS} /></L>
        </div>
        <L label="Competitor Activity"><textarea value={f.competitor_activity} onChange={upd("competitor_activity")} rows={2} className="w-full p-3 border border-slate-300 rounded-lg" /></L>
        <L label="Customer Feedback"><textarea value={f.customer_feedback} onChange={upd("customer_feedback")} rows={2} className="w-full p-3 border border-slate-300 rounded-lg" /></L>
        <L label="Issues Found"><textarea value={f.issues_found} onChange={upd("issues_found")} rows={2} className="w-full p-3 border border-slate-300 rounded-lg" /></L>
        <L label="Actions Required"><textarea value={f.actions_required} onChange={upd("actions_required")} rows={2} className="w-full p-3 border border-slate-300 rounded-lg" /></L>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h3 className="font-heading font-semibold">Opportunity & Follow-up</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <L label="Potential Sales Opportunity"><input value={f.potential_sales_opportunity} onChange={upd("potential_sales_opportunity")} className={inp} /></L>
          <L label="Estimated Opportunity Value (AUD)"><input type="number" step="0.01" value={f.estimated_opportunity_value} onChange={upd("estimated_opportunity_value")} className={inp} data-testid="visit-opportunity" /></L>
          <L label="Expected Order Date"><DatePicker value={f.expected_order_date} onChange={updDate("expected_order_date")} /></L>
          <L label="Follow-up Date"><DatePicker value={f.followup_date} onChange={updDate("followup_date")} /></L>
          <L label="Next Visit Date"><DatePicker value={f.next_visit_date} onChange={updDate("next_visit_date")} /></L>
          <L label="Visit Outcome"><Sh value={f.visit_outcome} onChange={updSelect("visit_outcome")} options={OUTCOMES} /></L>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.followup_required} onChange={upd("followup_required")} className="w-4 h-4" /> Follow-up required
        </label>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <h3 className="font-heading font-semibold">Photos</h3>
        <label className="block border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-[#003DA5] transition" data-testid="photo-upload-zone">
          <input type="file" multiple accept="image/*" onChange={addPhotos} className="hidden" data-testid="photo-input" />
          <Camera className="w-8 h-8 mx-auto text-slate-400" />
          <div className="mt-2 font-semibold">Take or upload photos</div>
        </label>
        {photos.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {photos.map((p, i) => (
              <div key={i} className="relative border border-slate-200 rounded-lg overflow-hidden">
                <img src={p.preview} className="w-full h-32 object-cover" alt="" />
                <Select value={p.category} onValueChange={(v) => { const cp = [...photos]; cp[i] = { ...cp[i], category: v }; setPhotos(cp); }}>
                  <SelectTrigger className="rounded-none border-0 border-t border-slate-200 text-xs h-9 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{PHOTO_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row justify-end gap-3">
        <button type="button" onClick={() => submit(true)} disabled={busy} className="h-12 px-6 rounded-lg border border-slate-300 font-semibold flex items-center gap-2 justify-center hover:border-[#003DA5]" data-testid="save-draft-btn">
          <Save className="w-4 h-4" />{busy ? "…" : "Save as Draft"}
        </button>
        <button type="submit" disabled={busy} className="mlily-btn h-12 px-8 rounded-lg font-semibold text-base flex items-center gap-2 justify-center" data-testid="submit-visit-btn">
          <Upload className="w-4 h-4" />{busy ? "Saving…" : editing ? "Submit Report" : "Submit Visit"}
        </button>
      </div>
    </form>
  );
}
